import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Logout as LogoutIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { User } from '../../types';
import avatarService from '../../services/avatarService';

interface SystemLockOverlayProps {
  open: boolean;
  lockedUser: Pick<User, 'id' | 'nome' | 'usuario' | 'perfil' | 'avatar'> | null;
  onUnlock: (password: string) => Promise<void>;
  onLogout: () => void | Promise<void>;
}

const SystemLockOverlay: React.FC<SystemLockOverlayProps> = ({ open, lockedUser, onUnlock, onLogout }) => {
  const theme = useTheme();
  const passwordRef = useRef<HTMLInputElement>(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const avatarUrl = avatarService.getAvatarUrl(lockedUser?.avatar);
  const initials = avatarService.getInitials(lockedUser?.nome || '');

  useEffect(() => {
    if (open) {
      setPassword('');
      setError('');
      window.setTimeout(() => passwordRef.current?.focus(), 100);
    }
  }, [open]);

  if (!open || !lockedUser) {
    return null;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!password.trim()) {
      setError('Digite sua senha para desbloquear.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onUnlock(password);
      setPassword('');
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Senha incorreta. Tente novamente.';
      setError(message);
      passwordRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      role="dialog"
      aria-modal="true"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: theme.zIndex.modal + 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        background:
          theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #101418 0%, #1c2430 52%, #29303a 100%)'
            : 'linear-gradient(135deg, #eef4f8 0%, #d9e6ee 52%, #c9d7df 100%)',
      }}
    >
      <Paper
        elevation={16}
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: '100%',
          maxWidth: 420,
          borderRadius: 2,
          p: { xs: 3, sm: 4 },
          textAlign: 'center',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            width: 54,
            height: 54,
            mx: 'auto',
            mb: 2,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          <LockIcon />
        </Box>

        <Typography variant="h5" fontWeight={700} gutterBottom>
          Sistema bloqueado
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Informe sua senha para continuar.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, textAlign: 'left' }}>
          <Avatar src={avatarUrl} sx={{ width: 52, height: 52, bgcolor: 'primary.main' }}>
            {!avatarUrl && initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {lockedUser.nome}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {lockedUser.usuario}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
            {error}
          </Alert>
        )}

        <TextField
          inputRef={passwordRef}
          fullWidth
          label="Senha"
          type={showPassword ? 'text' : 'password'}
          value={password}
          disabled={loading}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  onClick={() => setShowPassword((value) => !value)}
                  disabled={loading}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          fullWidth
          type="submit"
          variant="contained"
          size="large"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <LockOpenIcon />}
          disabled={loading}
          sx={{ mt: 3, minHeight: 46 }}
        >
          Desbloquear
        </Button>

        <Button
          fullWidth
          color="inherit"
          startIcon={<LogoutIcon />}
          onClick={onLogout}
          disabled={loading}
          sx={{ mt: 1.5 }}
        >
          Sair
        </Button>
      </Paper>
    </Box>
  );
};

export default SystemLockOverlay;
